/** @jsx jsx */
import { useState, useEffect } from 'react';
import { jsx } from '@emotion/core';
import { Input } from 'antd';
import moment from 'moment';
import { EditableFormTable } from '../../../_shared/editable-table';
import { TableListHeader } from '../../../_shared/table-list';
import { Button } from '../../../_shared/button';
import { size } from 'lodash';
import { exportTable, guid, mergeTableEdits, searchTable, withSorters } from '../../../actions/utils';
import { deleteDriver, getDrivers, updateDriver } from '../../../_shared/services/drivers.service';

// Card #242 — the two optional licence expiration dates. They are real columns
// on the `drivers` table holding a plain YYYY-MM-DD calendar string (no time,
// no zone), and POST /drivers/update writes the posted object straight into
// those columns, so the moment antd's DatePicker hands back has to be
// normalised before it leaves here.
// No extra role gate: these behave exactly like the other roster columns —
// editable by anyone who can already reach the Drivers tab (role 2 is already
// excluded by the tab itself in ../index.js).
export const EXP_FIELDS = ['licExp', 'occLicExp'];

const toDateString = (val) => {
  if (!val) { return null; }
  try {
    const m = moment.isMoment(val) ? val : moment(val);
    return m && m.isValid() ? m.format('YYYY-MM-DD') : null;
  } catch (err) {
    return null;
  }
};

export const normalizeDriverDates = (driver) => {
  const out = { ...driver };
  EXP_FIELDS.forEach(f => { out[f] = toDateString(driver[f]); });
  return out;
};

export const COLS = [
  {
    title: 'Name',
    dataIndex: 'name',
    editable: true
  },
  {
    title: 'Phone',
    dataIndex: 'phone',
    editable: true
  },
  {
    title: 'Email',
    dataIndex: 'email',
    editable: true
  },
  {
    title: 'Drivers License',
    dataIndex: 'lic',
    editable: true
  },
  {
    title: 'Drivers License Expiration',
    dataIndex: 'licExp',
    editable: true,
    required: false,
    inputType: 'date'
  },
  {
    title: 'Occupation Lic/Employee ID',
    dataIndex: 'occLic',
    editable: true
  },
  {
    title: 'Occupational License Expiration',
    dataIndex: 'occLicExp',
    editable: true,
    required: false,
    inputType: 'date'
  },
]

// Card #277 round 3 — the grid's columns, each with a sorter. The two expiration dates are
// 'date' cells, so they sort by date rather than as MM/DD/YYYY text. Search and export
// keep reading COLS itself.
const SORTED_COLS = withSorters(COLS);

export const Drivers = props => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');

  const getData = async () => {
    const data = await getDrivers();
    setRows(data);
  }

  useEffect(() => { getData(); }, []);

  // Card #277 — every column on screen, including both expiration dates in the localised
  // form the cells show them in.
  const shown = searchTable(COLS, rows, search);

  const updateData = async d => {
    // Normalise before both the local state update and the POST, so the table
    // never keeps a moment object around for the next save either.
    const normalized = d.map(normalizeDriverDates);

    // EditableFormTable hands back only the rows it was given to render, which under a
    // search is a subset — so the roster has to be stitched back together before the
    // delete diff runs, or saving one driver mid-search would delete every hidden one.
    const { data, removed } = mergeTableEdits(rows, shown, normalized);

    setRows(data);
    await Promise.all(normalized.map(async dd => {
      await updateDriver(dd)
    }))

    if (!!size(removed)) {
      await Promise.all(removed.map(async dd => {
        await deleteDriver(dd)
      }))
    }
  }

  // Card #287 — the sheet is built from COLS, the same definitions the table renders, so
  // it carries every column on screen (including the two expiration dates, formatted the
  // way the cells show them) and cannot drift as columns are added.
  const exportData = () => {
    exportTable(COLS, rows, { sheetName: 'Drivers' });
  }

  const addRow = () => {
    // Clear the search first: the blank row has nothing to match, so it would be added
    // out of sight and the button would look broken.
    setSearch('');
    setRows([
      ...rows,
      {
        id: guid(),
        name: '',
        phone: '',
        email: '',
        lic: '',
        occLic: '',
        // null (not '') — knex would otherwise have to bind an empty string
        // into the date columns, and null is what "not set" means here.
        licExp: null,
        occLicExp: null,
      }
    ])
  }

  return (
    <div>
      <TableListHeader>
        <Input.Search
          placeholder="Search all columns"
          value={search}
          allowClear
          style={{ width: '40%' }}
          onChange={e => setSearch(e.target.value)}
        />
        <Button
          icon="export"
          style={{ marginLeft: 'auto' }}
          title="Export Drivers"
          disabled={!size(rows)}
          onClick={exportData}
        />
      </TableListHeader>
      <EditableFormTable
        data={shown}
        columns={SORTED_COLS}
        updateData={updateData}
        addRow={addRow}
        rowKey={'id'}
        addButton={'Add Driver'}
      />
    </div>
  )
}
